/**
 * ArrayBuffer Utilities
 * 
 * Efficient conversion utilities for handling large binary data without
 * overwhelming browser memory or function argument limits.
 * 
 * Follows SOLID principles:
 * - Single Responsibility: Each function has one clear purpose
 * - Open/Closed: Extensible without modification
 * - Interface Segregation: Focused, minimal interfaces
 */

/**
 * Converts ArrayBuffer to Base64 string using chunked processing
 * 
 * This approach prevents:
 * 1. "Too many function arguments" error (by processing in chunks)
 * 2. Memory explosion (by avoiding intermediate string concatenation)
 * 3. Browser freezing (by using reasonable chunk sizes)
 * 
 * @param {ArrayBuffer} arrayBuffer - The binary data to convert
 * @param {number} chunkSize - Size of each processing chunk in bytes (default: 8192)
 * @returns {string} Base64 encoded string
 * @throws {Error} If arrayBuffer is invalid or conversion fails
 */
export const arrayBufferToBase64 = (arrayBuffer, chunkSize = 8192) => {
  if (!arrayBuffer || !(arrayBuffer instanceof ArrayBuffer)) {
    throw new Error('Invalid ArrayBuffer provided');
  }

  // Handle empty buffer
  if (arrayBuffer.byteLength === 0) {
    return '';
  }

  // For small files, use the direct approach for better performance
  if (arrayBuffer.byteLength <= chunkSize) {
    try {
      const uint8Array = new Uint8Array(arrayBuffer);
      const binaryString = String.fromCharCode.apply(null, uint8Array);
      return btoa(binaryString);
    } catch (error) {
      // Fallback to chunked approach if direct method fails
    }
  }

  // Chunked approach for large files
  const uint8Array = new Uint8Array(arrayBuffer);
  const chunks = [];
  
  for (let i = 0; i < uint8Array.length; i += chunkSize) {
    const end = Math.min(i + chunkSize, uint8Array.length);
    const chunk = uint8Array.subarray(i, end);
    
    // Convert chunk to binary string
    let binaryString = '';
    for (let j = 0; j < chunk.length; j++) {
      binaryString += String.fromCharCode(chunk[j]);
    }
    
    chunks.push(binaryString);
  }
  
  // Join all chunks and convert to base64
  const fullBinaryString = chunks.join('');
  return btoa(fullBinaryString);
};

/**
 * Converts Base64 string back to ArrayBuffer
 * 
 * @param {string} base64String - The base64 encoded string
 * @returns {ArrayBuffer} The decoded binary data
 * @throws {Error} If base64String is invalid
 */
export const base64ToArrayBuffer = (base64String) => {
  if (base64String === null || base64String === undefined || typeof base64String !== 'string') {
    throw new Error('Invalid base64 string provided');
  }
  
  // Handle empty string
  if (base64String === '') {
    return new ArrayBuffer(0);
  }

  try {
    const binaryString = atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return bytes.buffer;
  } catch (error) {
    throw new Error(`Failed to decode base64 string: ${error.message}`);
  }
};

/**
 * Gets the estimated memory usage for a given ArrayBuffer size
 * 
 * @param {number} arrayBufferSize - Size of the ArrayBuffer in bytes
 * @returns {object} Memory usage breakdown
 */
export const getMemoryUsageEstimate = (arrayBufferSize) => {
  const base64Size = Math.ceil(arrayBufferSize * 4 / 3); // Base64 overhead
  const stringMemory = base64Size * 2; // UTF-16 overhead
  const totalPeakMemory = arrayBufferSize + stringMemory; // Peak usage during conversion
  
  return {
    originalSize: arrayBufferSize,
    base64Size,
    stringMemoryUsage: stringMemory,
    totalPeakMemory,
    compressionRatio: base64Size / arrayBufferSize
  };
};

/**
 * Validates if an ArrayBuffer can be safely converted to Base64
 * 
 * @param {ArrayBuffer} arrayBuffer - The buffer to validate
 * @param {number} maxSizeMB - Maximum allowed size in MB (default: 100MB)
 * @returns {object} Validation result with recommendations
 */
export const validateArrayBufferSize = (arrayBuffer, maxSizeMB = 100) => {
  if (!arrayBuffer || !(arrayBuffer instanceof ArrayBuffer)) {
    return {
      isValid: false,
      error: 'Invalid ArrayBuffer provided',
      canProceed: false
    };
  }

  const sizeBytes = arrayBuffer.byteLength;
  const sizeMB = sizeBytes / (1024 * 1024);
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  
  const memoryEstimate = getMemoryUsageEstimate(sizeBytes);
  const peakMemoryMB = memoryEstimate.totalPeakMemory / (1024 * 1024);
  
  return {
    isValid: sizeBytes <= maxSizeBytes,
    canProceed: peakMemoryMB < 500, // Conservative 500MB browser limit
    sizeBytes,
    sizeMB: Math.round(sizeMB * 100) / 100,
    maxSizeMB,
    peakMemoryMB: Math.round(peakMemoryMB * 100) / 100,
    recommendation: sizeMB > 50 
      ? 'Consider using streaming or chunked download for better performance'
      : 'File size is acceptable for direct conversion',
    memoryEstimate
  };
}; 